import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import './ConfigTempoPage.css';

const EMPTY_TIER = { min: '', max: '', points: '' };

// Ancien barème (bornes partagées, ex. 0-5 / 5-10) → tranches à bornes incluses sans chevauchement (0-4 / 5-9)
const tiersFromLegacy = (legacyTiers = []) => {
    const sorted = [...legacyTiers].sort((a, b) => a.min - b.min);
    return sorted.map((tier, i) => {
        const next = sorted[i + 1];
        let max = tier.max ?? null;
        if (max !== null && next && next.min === max) max -= 1;
        return { min: String(tier.min ?? 0), max: max === null ? '' : String(max), points: String(tier.points ?? 0) };
    });
};

const toApiTier = (tier) => ({
    min: Number(tier.min),
    max: tier.max === '' ? null : Number(tier.max),
    points: Number(tier.points),
});

// Points obtenus pour une durée donnée (bornes incluses), comme le calcule le bot
const pointsFor = (minutes, tiers) => {
    const tier = tiers.map(toApiTier).find(t => minutes >= t.min && (t.max === null || minutes <= t.max));
    return tier ? tier.points : 0;
};

const ConfigTempoPage = () => {
    const { serverId } = useParams();
    const { success, error: showError, warning } = useNotification();
    const { t } = useLanguage();

    const [config, setConfig] = useState(null);
    const [channels, setChannels] = useState([]);
    const [channelId, setChannelId] = useState('');
    const [tiers, setTiers] = useState([{ ...EMPTY_TIER }]);
    const [previewMinutes, setPreviewMinutes] = useState('45');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (!serverId) return;
        const load = async () => {
            setLoading(true);
            try {
                const [tempoRes, channelsRes] = await Promise.all([
                    api.get(`/tempo/${serverId}`).catch(() => ({ data: null })),
                    api.get(`/discord/channels/${serverId}`).catch(async () => {
                        const serverRes = await api.get(`/servers/${serverId}`).catch(() => ({ data: {} }));
                        return { data: serverRes.data?.salons || [] };
                    }),
                ]);
                const cfg = tempoRes.data;
                setConfig(cfg);
                setChannels((Array.isArray(channelsRes.data) ? channelsRes.data : [])
                    .filter(c => c.type === 0)
                    .sort((a, b) => a.name.localeCompare(b.name)));

                if (cfg?.mode === 'minutes') {
                    setChannelId(cfg.minutesChannelId || '');
                    setTiers(cfg.minuteTiers?.length
                        ? cfg.minuteTiers.map(t => ({ min: String(t.min), max: t.max === null ? '' : String(t.max), points: String(t.points) }))
                        : [{ ...EMPTY_TIER }]);
                } else if (cfg?.timeTiers?.length) {
                    // Première activation : on part de l'ancien barème
                    setTiers(tiersFromLegacy(cfg.timeTiers));
                }
            } catch (err) {
                console.error('❌ Erreur chargement configuration tempo :', err);
                showError(t('tempoAdmin.loadError', 'Erreur lors du chargement de la configuration tempo'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [serverId, showError, t]);

    const updateTier = (index, field, value) => {
        setTiers(prev => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
        setDirty(true);
    };

    const addTier = () => {
        setTiers(prev => {
            const last = prev[prev.length - 1];
            const nextMin = last && last.max !== '' ? String(Number(last.max) + 1) : '';
            return [...prev, { ...EMPTY_TIER, min: nextMin }];
        });
        setDirty(true);
    };

    const removeTier = (index) => {
        setTiers(prev => prev.filter((_, i) => i !== index));
        setDirty(true);
    };

    const handleSave = async () => {
        if (!channelId) { warning(t('tempoAdmin.pickChannel', 'Choisissez le salon tempo')); return; }
        if (tiers.length === 0 || tiers.some(tier => tier.min === '' || tier.points === '')) {
            warning(t('tempoAdmin.tierIncomplete', 'Chaque tranche doit avoir un minimum et un nombre de points'));
            return;
        }
        setSaving(true);
        try {
            const channel = channels.find(c => c.id === channelId);
            const res = await api.put(`/tempo/${serverId}/minutes`, {
                channelId,
                channelName: channel?.name || channelId,
                minuteTiers: tiers.map(toApiTier),
            });
            setConfig(res.data);
            setDirty(false);
            success(config?.mode === 'minutes' ? t('tempoAdmin.saved', 'Configuration tempo enregistrée') : t('tempoAdmin.activated', 'Nouveau système tempo activé'));
        } catch (err) {
            showError(err.response?.data?.error || t('tempoAdmin.saveError', 'Erreur lors de la sauvegarde'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('tempoAdmin.loading', 'Chargement de la configuration tempo...')}</p>
                </div>
            </div>
        );
    }

    const isActive = config?.mode === 'minutes';
    const hasLegacy = (config?.timeTiers?.length || 0) > 0;
    const channelName = (id) => channels.find(c => c.id === id)?.name || config?.channels?.find(c => c.id === id)?.name || id;
    const preview = Number(previewMinutes);

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="clock"
                title={t('navigation.tempo', 'Tempo')}
                subtitle={t('tempoAdmin.subtitle', 'Points attribués selon la durée des combats')}
                actions={
                    <button type="button" className="c2-btn c2-btn--primary" onClick={handleSave} disabled={saving || (isActive && !dirty)}>
                        <Icon name="save" size={18} />
                        {saving ? t('tempoAdmin.saving', 'Enregistrement...') : isActive ? t('tempoAdmin.save', 'Enregistrer') : t('tempoAdmin.activate', 'Activer le nouveau système')}
                    </button>
                }
            />

            {/* État du système */}
            {isActive ? (
                <p className="c2-status tempo-state">
                    <Icon name="check" size={16} />
                    {t('tempoAdmin.activeChannel', 'Nouveau système actif · salon #{channel}', { channel: config.minutesChannelName || channelName(config.minutesChannelId) })}
                    {dirty && <span className="c2-chip tempo-unsaved">{t('tempoAdmin.unsaved', 'Modifications non enregistrées')}</span>}
                </p>
            ) : (
                <section className="c2-card tempo-notice">
                    <Icon name="clock" size={22} />
                    <div>
                        <h2 className="c2-section__title">{t('tempoAdmin.switchTitle', 'Passage au nouveau système')}</h2>
                        <p className="c2-section__desc">
                            {t('tempoAdmin.switchDesc', 'Un seul salon : les membres postent leur screen avec la durée, et les points dépendent de la tranche.')}
                            {hasLegacy && ` ${t('tempoAdmin.switchLegacy', 'Les screens déjà comptés avec l\'ancien système (un salon par tranche) gardent leurs points dans toutes les saisons.')}`}
                        </p>
                    </div>
                </section>
            )}

            <div className="tempo-layout">
                <div className="tempo-main">
                    {/* Salon */}
                    <section className="c2-card c2-section">
                        <h2 className="c2-section__title"><Icon name="hash" size={20} />{t('tempoAdmin.channelTitle', 'Salon tempo')}</h2>
                        <label className="c2-field tempo-channel">
                            <span className="c2-field__label">{t('tempoAdmin.discordChannel', 'Salon Discord')}</span>
                            <select className="c2-select" value={channelId} onChange={(e) => { setChannelId(e.target.value); setDirty(true); }}>
                                <option value="">{t('tempoAdmin.selectChannel', '-- Sélectionner un salon --')}</option>
                                {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                            </select>
                        </label>
                    </section>

                    {/* Barème */}
                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="scale" size={20} />{t('tempoAdmin.tiersTitle', 'Barème par durée')}</h2>
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={addTier}>
                                <Icon name="plus" size={16} />
                                {t('tempoAdmin.addTier', 'Ajouter une tranche')}
                            </button>
                        </div>
                        <p className="c2-section__desc">{t('tempoAdmin.tiersDesc', 'Bornes incluses. Laissez le maximum vide pour « et plus ».')}</p>

                        <ol className="tempo-tiers">
                            {tiers.map((tier, index) => (
                                <li key={index} className="tempo-tier">
                                    <span className="tempo-tier__index">{index + 1}</span>
                                    <label className="tempo-tier__field">
                                        <span>{t('tempoAdmin.from', 'De')}</span>
                                        <input className="c2-input c2-input--sm" type="number" min="0" value={tier.min} onChange={(e) => updateTier(index, 'min', e.target.value)} aria-label={t('tempoAdmin.tierMin', 'Tranche {n} : minimum en minutes', { n: index + 1 })} />
                                    </label>
                                    <label className="tempo-tier__field">
                                        <span>{t('tempoAdmin.to', 'à')}</span>
                                        <input className="c2-input c2-input--sm" type="number" min="0" placeholder="∞" value={tier.max} onChange={(e) => updateTier(index, 'max', e.target.value)} aria-label={t('tempoAdmin.tierMax', 'Tranche {n} : maximum en minutes', { n: index + 1 })} />
                                        <span>min</span>
                                    </label>
                                    <Icon name="arrowRight" size={16} className="tempo-tier__arrow" />
                                    <label className="tempo-tier__field">
                                        <input className="c2-input c2-input--sm tempo-tier__points" type="number" min="0" value={tier.points} onChange={(e) => updateTier(index, 'points', e.target.value)} aria-label={t('tempoAdmin.tierPoints', 'Tranche {n} : points', { n: index + 1 })} />
                                        <span>pts</span>
                                    </label>
                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => removeTier(index)} aria-label={t('tempoAdmin.deleteTier', 'Supprimer la tranche {n}', { n: index + 1 })} disabled={tiers.length === 1}>
                                        <Icon name="trash" size={16} />
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </section>
                </div>

                <aside className="tempo-side">
                    {/* Simulation */}
                    <section className="c2-card c2-section">
                        <h2 className="c2-section__title"><Icon name="clock" size={20} />{t('tempoAdmin.simulation', 'Simulation')}</h2>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('tempoAdmin.fightDuration', 'Durée du combat (minutes)')}</span>
                            <input className="c2-input" type="number" min="0" value={previewMinutes} onChange={(e) => setPreviewMinutes(e.target.value)} />
                        </label>
                        <p className="tempo-preview">
                            {previewMinutes === '' || Number.isNaN(preview)
                                ? '—'
                                : <><strong>{pointsFor(preview, tiers)}</strong> {t('tempoAdmin.points', 'points')}</>}
                        </p>
                    </section>

                    {/* Mode d'emploi */}
                    <section className="c2-card c2-section">
                        <h2 className="c2-section__title"><Icon name="user" size={20} />{t('tempoAdmin.forMembers', 'Pour les membres')}</h2>
                        <p className="c2-section__desc">
                            {t('tempoAdmin.howTo', 'Dans le salon tempo, poster le screen avec la durée et mentionner les joueurs. Chaque joueur mentionné reçoit les points.')}
                        </p>
                        <p className="tempo-example">{t('tempoAdmin.example', '@Joueur1 @Joueur2 1h30')}</p>
                        <p className="c2-field__hint">
                            {t('tempoAdmin.formats', 'Formats acceptés : 45 · 45 min · 1h · 1h30. Le bot répond ⏱️ quand c\'est compté, ❓ s\'il ne trouve pas la durée.')}
                        </p>
                    </section>
                </aside>
            </div>

            {/* Ancien système, conservé pour l'historique */}
            {hasLegacy && (
                <details className="c2-card tempo-legacy">
                    <summary>
                        <Icon name="history" size={18} />
                        {t('tempoAdmin.legacyTitle', 'Ancien barème par salon (conservé pour les anciens screens)')}
                        <Icon name="chevronDown" size={18} className="tempo-legacy__chevron" />
                    </summary>
                    <div className="c2-table-wrap">
                        <table className="c2-table">
                            <thead>
                                <tr><th>{t('tempoAdmin.channel', 'Salon')}</th><th>{t('tempoAdmin.tier', 'Tranche')}</th><th className="is-num">{t('tempoAdmin.pointsCol', 'Points')}</th></tr>
                            </thead>
                            <tbody>
                                {config.timeTiers.map((tier, i) => (
                                    <tr key={i}>
                                        <td>#{channelName(tier.channelId)}</td>
                                        <td className="c2-muted">{tier.label || `${tier.min} – ${tier.max ?? '∞'} min`}</td>
                                        <td className="is-num">{tier.points}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </div>
    );
};

export default ConfigTempoPage;
